<template>
  <div
    v-if="state.roomInfo.length > 0"
    class="row dashboard-widget"
    @scroll="onScroll"
  >
    <div
      class="col-xl-6 col-xs-12 col-md-6"
      v-for="item of state.roomInfo"
      :key="item.id"
      :class="item.textColor"
    >
      <div class="card card-animate">
        <div class="card-body" :class="`${item.backgroundClass} rd6`" :style="is2K ? 'padding-top: 1.8rem; padding-bottom: 1.8rem;' : ''">
          <div class="d-flex align-items-center">
            <div class="avatar-sm flex-shrink-0">
              <span
                class="avatar-title rounded-2 fs-2"
                :class="{
                  'badge-white text-primary': item.feaIconClass,
                  'badge-grey text-warning': !item.feaIconClass,
                }"
              >
                <template v-if="item.feaIcon">
                  <img :src="item.feaIcon" alt="" class="room-icon" />
                </template>
              </span>
            </div>
            <div class="flex-grow-1 overflow-hidden ms-3">
              <p class="fw-medium text-truncate mb-3">
                {{
                  facilityInfo?.type !== "SAUNA"
                    ? getRoomDisplayName(item)
                    : `${$t(item.label)}：${item.description}`
                }}
              </p>
              <div
                class="d-flex align-items-center mb-3 w-100 justify-content-between"
              >
                <div class="widget-content">
                  <span class="content-title mr-2">
                    {{ formatter(item.counter) }}
                  </span>
                  <span>{{
                    `${$t("t-name2")} / ${
                      item.subLabel ? formatter(item.subLabel) : "- "
                    }${$t("t-name2")}`
                  }}</span>
                </div>
                <span
                  class="badge badge-widget fs-14"
                  :class="{
                    'badge-white': item.backgroundClass,
                    'badge-grey': !item.backgroundClass,
                  }"
                >
                  <span>{{ item.percent }}</span>
                </span>
              </div>
              <p class="text-truncate mb-0">
                {{
                  $t("totalPeopleUse", { total: formatter(item.personComeIn) })
                }}
              </p>
            </div>
          </div>
        </div>
        <!-- end card body -->
      </div>
    </div>
  </div>
</template>

<script>
import { round, sum } from "lodash";
import { ref, onMounted, watch, computed, reactive } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { sensorLogsService } from "../../../services";
import { getFacilitySelected } from "@/utils/facility-account";

export default {
  props: {
    facilityId: {
      type: String,
      default: "",
    },
    latestRoomData: {
      type: Array,
      default: () => [],
    },
    is2K: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const projectsWidgets = ref([]);
    const facilityInfo = ref(null);

    const store = useStore();
    const route = useRoute();
    const loading = ref(false);

    const totalPage = ref(0);
    const currentPage = ref(0);
    const intervalRefreshData = ref();

    const refreshData = computed(
      () => store.getters["settings/getRefreshData"]
    );

    const secondRefresh = computed(() => store.getters["settings/getSeconds"]);

    const params = reactive({
      page: 1,
      pageSize: 10,
      subType: "SAUNA_DRIED",
      facilityId: props.facilityId,
    });

    const state = reactive({
      roomInfo: [],
      hasReceivedChartData: false, // Track if chart data has been received
    });

    /**
     *
     * @param {Number} percent
     */
    const getBackgroundByPercent = (percent) => {
      if (percent < 40) return "bg-info";
      else if (percent >= 40 && percent < 60) return "bg-success";
      else if (percent >= 60 && percent < 80) return "bg-warning";
      else return "bg-danger";
    };

    const getTextRoomType = (roomType) => {
      if (roomType === "SAUNA_ROOM_MALE") return "t-men-bath-room";
      else if (roomType === "SAUNA_ROOM_FEMALE") return "t-women-bath-room";
      else {
        return "OTHER";
      }
    };

    const getRoomsInfo = async (type) => {
      if (!props.facilityId) return;
      try {
        if (!type || type !== "refresh") {
          loading.value = true;
        }
        const res = await sensorLogsService.getRoomsInfo({
          facilityId: props.facilityId,
        });

        // If chart data has been received, preserve the counter from chart data
        if (state.hasReceivedChartData && state.roomInfo.length > 0) {
          // console.log("getRoomsInfo: Preserving counter from chart data");
          state.roomInfo = res.map((d) => {
            // Find existing room to preserve counter from chart
            const existingRoom = state.roomInfo.find(
              (r) => r.roomName === d.roomName
            );

            return {
              ...d,
              feaIcon:
                facilityInfo.value?.type === "SAUNA"
                  ? require("@/assets/images/dashboard/img345.png")
                  : require("@/assets/images/dashboard/img345.svg"),
              feaIconClass: "success",
              textColor: "text-white",
              label: getTextRoomType(d.type),
              counter: existingRoom
                ? existingRoom.counter
                : d.currentPeopleCount, // Keep chart counter if exists
              subLabel: d.capacity,
              description: d.roomName,
              suffix: "",
              badgeClass: "danger",
              icon: "ri-arrow-down-s-line",
              percent: existingRoom
                ? existingRoom.percent
                : `${round(d.percent)}%`, // Keep chart percent if exists
              backgroundClass: existingRoom
                ? existingRoom.backgroundClass
                : getBackgroundByPercent(d.percent),
              personComeIn: d.personComeIn || 0, // Always update personComeIn from API
            };
          });
        } else {
          // First load or no chart data yet, use getRoomsInfo data
          // console.log("getRoomsInfo: Using API data (no chart data yet)");
          state.roomInfo = res.map((d) => ({
            ...d,
            feaIcon:
              facilityInfo.value?.type === "SAUNA"
                ? require("@/assets/images/dashboard/img345.png")
                : require("@/assets/images/dashboard/img345.svg"),
            feaIconClass: "success",
            textColor: "text-white",
            label: getTextRoomType(d.type),
            counter: d.currentPeopleCount,
            subLabel: d.capacity,
            description: d.roomName,
            suffix: "",
            badgeClass: "danger",
            icon: "ri-arrow-down-s-line",
            percent: `${round(d.percent)}%`,
            backgroundClass: getBackgroundByPercent(d.percent),
            personComeIn: d.personComeIn || 0,
          }));
        }
        loading.value = false;
      } catch (e) {
        state.roomInfo = [];
        loading.value = false;
      }

      const peopleIn = sum(state.roomInfo.map((r) => r.personComeIn));
      emit("peopleIn", peopleIn);
      emit("getHasRooms", state.roomInfo.length > 0);
    };

    const formatter = (quantity) =>
      new Intl.NumberFormat("en-US").format(quantity);

    watch(
      route,
      (to) => {
        if (to.name.value !== "default") {
          clearInterval(intervalRefreshData.value);
        }
      },
      { flush: "pre", immediate: true, deep: true }
    );

    watch(refreshData, (data) => {
      if (!data) {
        clearInterval(intervalRefreshData.value);
      } else {
        if (
          refreshData.value &&
          secondRefresh.value &&
          secondRefresh.value * 1 > 29
        ) {
          intervalRefreshData.value = setInterval(() => {
            //getRoomsDashboard('refresh');
            getRoomsInfo("refresh");
          }, secondRefresh.value * 1000);
        } else {
          clearInterval(intervalRefreshData.value);
        }
      }
    });

    onMounted(() => {
      if (
        refreshData.value &&
        secondRefresh.value &&
        secondRefresh.value * 1 > 29
      ) {
        intervalRefreshData.value = setInterval(() => {
          //getRoomsDashboard('refresh');
          getRoomsInfo("refresh");
        }, secondRefresh.value * 1000);
      } else {
        clearInterval(intervalRefreshData.value);
      }
      //getRoomsDashboard();
      getRoomsInfo();
    });

    watch(
      () => props.facilityId,
      () => {
        projectsWidgets.value = [];
        //getRoomsDashboard()
        getRoomsInfo();

        facilityInfo.value = getFacilitySelected(props.facilityId);
      },
      { immediate: true }
    );

    watch(
      () => props.latestRoomData,
      (newData) => {
        if (newData && newData.length > 0 && state.roomInfo.length > 0) {
          // Mark that we've received chart data
          state.hasReceivedChartData = true;
          // console.log(
          //   "Chart data received: Updating counter/percent from chart"
          //);

          state.roomInfo = state.roomInfo.map((room) => {
            const latestData = newData.find(
              (d) => d.roomName === room.roomName
            );
            if (latestData) {
              const newCounter = latestData.currentPeopleCount;
              const newPercent = latestData.percentage;
              // console.log(
              //   `Chart updating ${room.roomName}: counter=${newCounter}, percent=${newPercent}%`
              // );
              return {
                ...room,
                counter: newCounter,
                percent: `${round(newPercent)}%`,
                backgroundClass: getBackgroundByPercent(newPercent),
              };
            }
            return room;
          });
        }
      },
      { deep: true }
    );

    // Function onScroll
    const onScroll = (e) => {
      const el = e.srcElement;

      if (
        !loading.value &&
        Math.abs(el.scrollLeft + 10) > el.scrollWidth - el.clientWidth
      ) {
        if (currentPage.value < totalPage.value) {
          params.page += 1;
        }
      }
    };

    const getRoomDisplayName = (item) => {
      if (item.type === "ROOM" || item.type === "ROOM_1") {
        return `ルーム1：${item.roomName}`;
      } else if (item.type === "ROOM_2") {
        return `ルーム2：${item.roomName}`;
      }
      return `ルーム1：${item.roomName}`;
    };

    return {
      facilityInfo,
      loading,
      projectsWidgets,
      onScroll,
      state,
      getRoomDisplayName,
      formatter,
    };
  },
  components: {},
};
</script>

<style lang="scss">
.dashboard-widget {
  font-size: 14px;
  flex-wrap: nowrap;
  overflow: auto;
  position: relative;

  .card {
    margin-bottom: 0.7rem;
    position: relative;
  }

  .widget-content {
    .content-title {
      font-size: 24px;
    }
  }

  .rd6 {
    border-radius: 6px;
  }

  .badge-widget {
    padding: 10px 20px;
  }

  .badge-white {
    background: rgba(255, 255, 255, 0.4);
    color: white;
  }

  .badge-grey {
    background: rgba(222, 219, 219, 0.4);
    color: #495057;
  }

  .bgMedium {
    background: #d59231;
  }

  .bgEmpty {
    background: #2d62ae;
  }

  .bgLarge {
    background: #c0272d;
  }

  .bgNormal {
    background: #3b7e44;
  }

  .room-icon {
    width: 70%;
    height: auto;
  }
}
</style>
